// Dashboard.js - unified Sales/Purchase/Order overview, permission-driven
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Divider,
  Chip,
  Skeleton,
  Fade,
  Slide,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  alpha,
} from "@mui/material";
import {
  RocketLaunch,
  ReceiptLong,
  Inventory2,
  LocalShipping,
  CheckCircleRounded,
  PendingActionsRounded,
  AutorenewRounded,
  CancelRounded,
  ShowChart,
  DonutLarge,
  Timeline as TimelineIcon,
  ArrowForwardIos,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { salesAPI, purchaseAPI, orderAPI, auditAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { STATUS_BUCKETS, bucketFor, MODULE_META } from "../utils/statusBuckets";

const prettyDate = (d) => (d ? new Date(d).toLocaleDateString() : "-");

const API_MAP = {
  sales_tracker: salesAPI,
  purchase_tracker: purchaseAPI,
  order_tracker: orderAPI,
};

const MODULE_ICONS = {
  sales_tracker: <ReceiptLong sx={{ fontSize: 32 }} />,
  purchase_tracker: <Inventory2 sx={{ fontSize: 32 }} />,
  order_tracker: <LocalShipping sx={{ fontSize: 32 }} />,
};

const BUCKET_ICONS = {
  good: <CheckCircleRounded sx={{ fontSize: 32 }} />,
  warning: <PendingActionsRounded sx={{ fontSize: 32 }} />,
  serious: <AutorenewRounded sx={{ fontSize: 32 }} />,
  critical: <CancelRounded sx={{ fontSize: 32 }} />,
};

const brandColors = {
  primary: "#005b82",
  secondary: "#2586bc",
};

// ---------- Stat Card ----------
const StatCard = ({ title, value, icon, color = "#005b82", loading = false }) => (
  <Card
    sx={{
      borderRadius: 4,
      height: "100%",
      background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
      border: `1px solid ${alpha(color, 0.2)}`,
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "hidden",
      "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: `0 12px 40px ${alpha(color, 0.2)}`,
        "&::before": { transform: "scaleX(1)" },
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.8)} 100%)`,
        transform: "scaleX(0)",
        transition: "transform 0.3s ease",
      },
    }}
  >
    <CardContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontSize: 12, fontWeight: 600, mb: 1 }}>
            {title}
          </Typography>
          {loading ? (
            <Skeleton width={80} height={32} />
          ) : (
            <Typography variant="h2" fontWeight={800} sx={{ color, lineHeight: 1 }}>
              {value ?? 0}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            color,
            p: 2,
            borderRadius: 3,
            background: alpha(color, 0.1),
            boxShadow: `0 4px 12px ${alpha(color, 0.2)}`,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const chartCardSx = {
  borderRadius: 4,
  background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255,255,255,0.3)",
  boxShadow: "0 8px 32px rgba(0,91,130,0.1)",
};

const Dashboard = () => {
  const { user, canReadModule } = useAuth();

  const [loading, setLoading] = useState(true);
  const [moduleData, setModuleData] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);

  const modules = useMemo(
    () => ["sales_tracker", "purchase_tracker", "order_tracker"].filter(canReadModule),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );
  const modulesKey = modules.join(",");

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          modules.map((m) => Promise.all([API_MAP[m].getStats(), API_MAP[m].getRecent()]))
        );
        if (!mounted) return;
        const next = {};
        modules.forEach((m, i) => {
          next[m] = { stats: results[i][0].data || {}, recent: results[i][1].data || [] };
        });
        setModuleData(next);

        if (user?.is_superuser) {
          try {
            const aRes = await auditAPI.getLogs();
            if (mounted) setAuditLogs(aRes.data || []);
          } catch {}
        }
      } catch (e) {
        console.error("Dashboard load error", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modulesKey, user?.is_superuser]);

  // Status-bucket totals, aggregated across every accessible module, plus a
  // module x bucket breakdown for the table under the donut.
  const { bucketTotals, moduleBucketMatrix } = useMemo(() => {
    const totals = { good: 0, warning: 0, serious: 0, critical: 0 };
    const matrix = {};
    modules.forEach((m) => {
      const stats = moduleData[m]?.stats || {};
      const row = { good: 0, warning: 0, serious: 0, critical: 0 };
      Object.entries(stats).forEach(([key, value]) => {
        if (key === "total") return;
        const bucket = bucketFor(key);
        row[bucket] += value;
        totals[bucket] += value;
      });
      matrix[m] = row;
    });
    return { bucketTotals: totals, moduleBucketMatrix: matrix };
  }, [modules, moduleData]);

  // Day-bucketed record counts per module, from each module's recent list -
  // this is a "recent activity" trend, not a true monthly aggregate (the
  // backend has no such endpoint), so it's labelled accordingly.
  const trendData = useMemo(() => {
    const dayMap = new Map();
    modules.forEach((m) => {
      (moduleData[m]?.recent || []).forEach((r) => {
        const dt = new Date(r.created_at);
        if (isNaN(dt)) return;
        const day = dt.toISOString().slice(0, 10);
        if (!dayMap.has(day)) dayMap.set(day, {});
        const entry = dayMap.get(day);
        entry[m] = (entry[m] || 0) + 1;
      });
    });
    return Array.from(dayMap.entries())
      .sort(([a], [b]) => (a > b ? 1 : -1))
      .map(([day, counts]) => {
        const row = { day };
        modules.forEach((m) => {
          row[m] = counts[m] || 0;
        });
        return row;
      });
  }, [modules, moduleData]);

  const pieData = useMemo(() => {
    const parts = Object.entries(bucketTotals).map(([key, value]) => ({
      name: STATUS_BUCKETS[key].label,
      value,
      color: STATUS_BUCKETS[key].color,
    }));
    const total = parts.reduce((a, b) => a + b.value, 0);
    return total === 0 ? [{ name: "No Data", value: 1, color: "#bdbdbd" }] : parts.filter((p) => p.value > 0);
  }, [bucketTotals]);

  const mergedRecent = useMemo(() => {
    const rows = [];
    modules.forEach((m) => {
      (moduleData[m]?.recent || []).forEach((r) => rows.push({ ...r, _module: m }));
    });
    return rows
      .filter((r) => r.created_at)
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
      .slice(0, 10);
  }, [modules, moduleData]);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <Fade in timeout={500}>
        <Box>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontSize: { xs: 16, md: 20 }, display: "flex", alignItems: "center", gap: 1 }}
            >
              <RocketLaunch sx={{ fontSize: 20, color: brandColors.secondary }} />
              Welcome back,{" "}
              <strong style={{ color: brandColors.primary }}>
                {user?.first_name} {user?.last_name}
              </strong>
            </Typography>
          </Box>

          {/* Per-module totals */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {modules.map((m, i) => (
              <Grid item xs={12} sm={6} md={4} key={m}>
                <Slide in direction="up" mountOnEnter unmountOnExit timeout={500 + i * 50}>
                  <div>
                    <StatCard
                      title={`Total ${MODULE_META[m].label}`}
                      value={moduleData[m]?.stats?.total}
                      icon={MODULE_ICONS[m]}
                      color={MODULE_META[m].color}
                      loading={loading}
                    />
                  </div>
                </Slide>
              </Grid>
            ))}
          </Grid>

          {/* Aggregated status-bucket totals */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {Object.entries(STATUS_BUCKETS).map(([key, bucket], i) => (
              <Grid item xs={12} sm={6} lg={3} key={key}>
                <Slide in direction="up" mountOnEnter unmountOnExit timeout={600 + i * 50}>
                  <div>
                    <StatCard
                      title={bucket.label}
                      value={bucketTotals[key]}
                      icon={BUCKET_ICONS[key]}
                      color={bucket.color}
                      loading={loading}
                    />
                  </div>
                </Slide>
              </Grid>
            ))}
          </Grid>

          {modules.length === 0 && !loading && (
            <Card sx={{ ...chartCardSx, mb: 4 }}>
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  You don't have read access to any tracker module yet. Ask your administrator to grant
                  access via the Permissions screen.
                </Typography>
              </CardContent>
            </Card>
          )}

          <Grid container spacing={3}>
            {/* Trend chart */}
            <Grid item xs={12} lg={8}>
              <Card sx={chartCardSx}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                    <ShowChart sx={{ fontSize: 24, color: brandColors.primary }} />
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{ fontSize: { xs: 16, md: 20 }, color: brandColors.primary }}
                    >
                      Recent Activity Trend
                    </Typography>
                  </Box>
                  <Box sx={{ height: { xs: 260, md: 300 } }}>
                    {loading ? (
                      <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 3 }} />
                    ) : trendData.length === 0 ? (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "text.secondary",
                        }}
                      >
                        No recent activity to chart
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trendData} margin={{ top: 16, right: 16, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={alpha(brandColors.primary, 0.1)} />
                          <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke={alpha(brandColors.primary, 0.7)} />
                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 11 }}
                            stroke={alpha(brandColors.primary, 0.7)}
                          />
                          <RTooltip
                            contentStyle={{
                              borderRadius: 8,
                              background: "rgba(255,255,255,0.95)",
                              border: `1px solid ${alpha(brandColors.primary, 0.2)}`,
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: "12px" }} />
                          {modules.map((m) => (
                            <Line
                              key={m}
                              type="monotone"
                              dataKey={m}
                              name={MODULE_META[m].label}
                              stroke={MODULE_META[m].color}
                              strokeWidth={3}
                              dot={false}
                              activeDot={{ r: 6 }}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Status distribution */}
            <Grid item xs={12} lg={4}>
              <Card sx={chartCardSx}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                    <DonutLarge sx={{ fontSize: 24, color: brandColors.primary }} />
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{ fontSize: { xs: 16, md: 20 }, color: brandColors.primary }}
                    >
                      Status Distribution
                    </Typography>
                  </Box>
                  <Box sx={{ height: { xs: 240, md: 260 } }}>
                    {loading ? (
                      <Skeleton variant="circular" height={180} width={180} sx={{ mx: "auto" }} />
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={({ width, height }) => (width < 400 ? 80 : 100)}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            labelLine={false}
                          >
                            {pieData.map((e, i) => (
                              <Cell key={`cell-${i}`} fill={e.color} />
                            ))}
                          </Pie>
                          <Legend wrapperStyle={{ fontSize: "12px", marginTop: "20px" }} />
                          <RTooltip
                            formatter={(value, name) => [value, name]}
                            contentStyle={{
                              borderRadius: 8,
                              background: "rgba(255,255,255,0.95)",
                              border: `1px solid ${alpha(brandColors.primary, 0.2)}`,
                            }}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    )}
                  </Box>

                  {!loading && modules.length > 0 && (
                    <Box sx={{ overflowX: "auto", mt: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                            {Object.entries(STATUS_BUCKETS).map(([key, b]) => (
                              <TableCell key={key} align="right" sx={{ fontWeight: 700, color: b.color }}>
                                {b.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {modules.map((m) => (
                            <TableRow key={m}>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={MODULE_META[m].label}
                                  sx={{
                                    background: alpha(MODULE_META[m].color, 0.15),
                                    color: MODULE_META[m].color,
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              {Object.keys(STATUS_BUCKETS).map((key) => (
                                <TableCell key={key} align="right">
                                  {moduleBucketMatrix[m]?.[key] ?? 0}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Merged recent activity */}
            {modules.length > 0 && (
              <Grid item xs={12}>
                <Card sx={chartCardSx}>
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ReceiptLong sx={{ fontSize: 24, color: brandColors.primary }} />
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          sx={{ fontSize: { xs: 16, md: 20 }, color: brandColors.primary }}
                        >
                          Recent Activity Across Modules
                        </Typography>
                      </Box>
                      {modules.includes("sales_tracker") && (
                        <Button
                          variant="contained"
                          endIcon={<ArrowForwardIos />}
                          onClick={() => (window.location.href = "/sales")}
                          sx={{
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
                          }}
                        >
                          View Sales
                        </Button>
                      )}
                    </Box>
                    {loading ? (
                      <>
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Box key={i} sx={{ my: 2 }}>
                            <Skeleton height={28} />
                            <Skeleton height={20} width="60%" />
                            <Divider sx={{ mt: 1.5 }} />
                          </Box>
                        ))}
                      </>
                    ) : mergedRecent.length === 0 ? (
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        No recent records yet.
                      </Typography>
                    ) : (
                      <Box sx={{ display: "grid", gap: 2 }}>
                        {mergedRecent.map((rec) => {
                          const meta = MODULE_META[rec._module];
                          const bucket = bucketFor(rec[meta.statusField]);
                          const bucketMeta = STATUS_BUCKETS[bucket];
                          return (
                            <Paper
                              key={`${rec._module}-${rec.id}`}
                              sx={{
                                p: 2,
                                borderRadius: 3,
                                border: `1px solid ${alpha(brandColors.primary, 0.1)}`,
                                background: alpha(brandColors.primary, 0.02),
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  border: `1px solid ${alpha(brandColors.primary, 0.3)}`,
                                  boxShadow: `0 4px 12px ${alpha(brandColors.primary, 0.1)}`,
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  flexWrap: "wrap",
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                    <Chip
                                      size="small"
                                      label={meta.label}
                                      sx={{
                                        background: alpha(meta.color, 0.15),
                                        color: meta.color,
                                        fontWeight: 600,
                                      }}
                                    />
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={600}
                                      sx={{ fontSize: { xs: 14, md: 16 } }}
                                    >
                                      {rec.sr_no} — {rec.vessel_name}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Created by: {rec.created_by_name} • {prettyDate(rec.created_at)}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={bucketMeta.label}
                                  size="small"
                                  sx={{
                                    background: alpha(bucketMeta.color, 0.15),
                                    color: bucketMeta.color,
                                    fontWeight: 600,
                                  }}
                                />
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Audit Log (superadmin only, mirrors backend gating) */}
            {user?.is_superuser && (
              <Grid item xs={12}>
                <Card sx={chartCardSx}>
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                      <TimelineIcon sx={{ fontSize: 24, color: brandColors.primary }} />
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ fontSize: { xs: 16, md: 20 }, color: brandColors.primary }}
                      >
                        System Activity Log
                      </Typography>
                    </Box>
                    {loading ? (
                      <>
                        {Array.from({ length: 6 }).map((_, i) => (
                          <Box key={i} sx={{ my: 2 }}>
                            <Skeleton height={22} />
                            <Skeleton height={18} width="60%" />
                            <Divider sx={{ mt: 1.5 }} />
                          </Box>
                        ))}
                      </>
                    ) : (
                      <Box sx={{ display: "grid", gap: 2 }}>
                        {auditLogs.slice(0, 10).map((log) => (
                          <Paper
                            key={log.id}
                            sx={{
                              p: 2,
                              borderRadius: 3,
                              border: `1px solid ${alpha(brandColors.primary, 0.1)}`,
                              background: alpha(brandColors.primary, 0.02),
                              transition: "all 0.2s ease",
                              "&:hover": {
                                border: `1px solid ${alpha(brandColors.primary, 0.2)}`,
                                background: alpha(brandColors.primary, 0.04),
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  {log.changed_by_name} changed{" "}
                                  <span style={{ color: brandColors.primary }}>{log.field_name}</span>
                                </Typography>
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
                                  <Chip
                                    label={`Old: ${log.old_value || "—"}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderColor: alpha(brandColors.primary, 0.3) }}
                                  />
                                  <Chip
                                    label={`New: ${log.new_value || "—"}`}
                                    size="small"
                                    sx={{ background: alpha(brandColors.primary, 0.1), color: brandColors.primary }}
                                  />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(log.timestamp).toLocaleString()} • {log.ip_address} • {log.screen}
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </Fade>
    </Container>
  );
};

export default Dashboard;
